import { noop } from 'lodash';
import { useState } from 'react';
import { useTimeoutFn, useToggle, useUpdateEffect } from 'react-use';

import {
   Advertisement,
   AdvertisementGenerateCommand,
   BannerLocation,
} from '@dreryk/egabinet-advertisement-frontend-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { Slot } from '@dreryk/watermelon';

import { BannerSize } from './Banner.types';
import { Banner } from './Banner';

const ONE_SECOND_MULTIPLIER = 1000;
const advertisementExpirationTimeInSeconds = (advertisement: Advertisement | null): number => {
   if (!advertisement) {
      return 0;
   }

   return advertisement.expirationTimeInSeconds * ONE_SECOND_MULTIPLIER;
};

type BannerCardProps = {
   readonly location: Extract<BannerLocation, 'DASHBOARD_TOP' | 'DASHBOARD_BOTTOM'>;
   readonly size?: BannerSize;
};

export const BannerCard = (props: BannerCardProps): JSX.Element | null => {
   const [bannerError, toggleBannerError] = useToggle(false);
   const [advertisement, setAdvertisement] = useState<Advertisement | null>(null);

   const { execute: generateAdvertisement, error: generateAdvertisementError } = useCqrsCommand<Advertisement, void>(
      () => {
         const command = new AdvertisementGenerateCommand();
         command.location = props.location;
         command.visitId = Slot.empty();
         return command;
      },
      {
         onSuccess: setAdvertisement,
         onUnexpectedError: noop,
         background: true,
      }
   );

   useUpdateEffect(generateAdvertisement, []);

   const [, , resetTimeout] = useTimeoutFn(generateAdvertisement, advertisementExpirationTimeInSeconds(advertisement));

   if (generateAdvertisementError || bannerError) {
      return null;
   }

   if (advertisement) {
      return (
         <Banner
            key={advertisement.id}
            testId={advertisement.id}
            bannerUrl={advertisement.bannerUrl}
            targetUrl={advertisement.advertisementUrl}
            onLoad={resetTimeout}
            onError={toggleBannerError}
            size={props.size}
         />
      );
   }

   return null;
};
